/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { Link, Redirect } from 'react-router-dom';
import InputForm from 'components/TradeHelper/InputForm';
import FollowupQuestionsDisplay from 'components/TradeHelper/FollowupQuestionsDisplay';
import { useWebSocketFetcher } from 'hooks/useWebSocketFetcher';
import { iFollowupItem } from 'interfaces/iFollowupItem';
import { iStreamedResponse } from 'interfaces/iStreamedResponse';
import StreamDisplay from 'components/TradeHelper/StreamDisplay';
import { Grid, Modal, Tooltip } from '@mui/material';
import DeleteCommonModal from 'components/Monitor/Modals/DeleteModal';
import { GlobalContext } from 'providers/GlobalProvider';
import CasesAPI from 'api/CasesAPI';
import { useSnackbar } from 'notistack';
import format from 'date-fns/format';
import CreateTicketWizard from 'components/Wizard/CreateTicketWizard';
import { LoadingFeedback } from 'components/Common/LoadingModal';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

const EARClassification: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();

  const dt = new Date();
  const dtWithoutTz = new Date(
    dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000,
  );
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const { streamData, isStreaming, connectWebSocket } =
    useWebSocketFetcher(apiUrl);

  const [userProductDesc, setUserProductDesc] = useState<string>('');
  const [userECCN, setUserECCN] = useState<any>([]);
  const [country, setCountry] = useState<string>('');
  const [followupItems, setFollowupItems] = useState<iFollowupItem[]>([]);
  const [clfId, setClfId] = useState<string | null>(null);
  const [cmdtDesc, setCmdtDesc] = useState<string | null>(null);
  const [HTSCode, setHTSCode] = useState<string | null>(null);
  const [explanatoryNotes, setExplanatoryNotes] = useState<string | null>(null);
  const [sectionNotes, setSectionNotes] = useState<string | null>(null);
  const [currentStage, setCurrentStage] = useState<number>(0);
  const [currentEStage, setCurrentEStage] = useState<number>(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExpandedCross, setIsExpandedCross] = useState(false);
  const [isExpandedSecNotes, setIsExpandedSecNotes] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savingTicket, setSavingTicket] = useState(false);
  const [ticketId, setTicketID] = useState('');
  const [autoTab, setAutoTab] = useState<string>('ECCN');
  const [owner, setOwner] = useState('');
  const [crossLinks, setCrossLinks] = useState<string[]>([]);
  // License Checking
  const [userHasClassify, setUserHasClassify] = useState(false);
  const [newTicketName, setNewTicketName] = useState<string>('');
  const [shareEmail, setShareEmail] = useState<string[]>([]);

  const requiredTiers = ['starter', 'pro', 'business', 'enterprise'];

  const checkForClassify = () => {
    if (
      requiredTiers.some((role) =>
        cur_user.selected_team?.attributes?.tier.includes(role),
      )
    ) {
      setUserHasClassify(true);
    }
  };

  useEffect(() => {
    checkForClassify();
    setOwner(cur_user.basics?.email || '');
  }, []);

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  // Process stream
  useEffect(() => {
    streamData.forEach((chunk) => {
      try {
        const classificationReq: iStreamedResponse = JSON.parse(chunk);
        const {
          followup_items,
          clf_id,
          hts_code,
          eccn_code,
          cmdt_desc,
          explanatory_notes,
          section_chapter_notes,
          cross_data_links,
        } = classificationReq;
        if (eccn_code) {
          setHTSCode(eccn_code);
        }
        if (cmdt_desc) {
          setCmdtDesc(cmdt_desc);
        }
        if (clf_id && clf_id.trim() !== '') {
          setClfId((prev) => (prev === clf_id ? prev : clf_id));
        }

        if (followup_items && followup_items.length > 0) {
          setFollowupItems((prev) => {
            const existingQuestions = new Set(
              prev.map((item) => item.question),
            );
            const newItems = followup_items.filter(
              (item) => !existingQuestions.has(item.question),
            );
            return [...prev, ...newItems];
          });
        }
      } catch (err) {
        console.warn('Invalid JSON chunk in useEffect:', chunk, err);
      }
    });
  }, [streamData]);

  // Handle default stages
  useEffect(() => {
    if (followupItems.length > 0) {
      setCurrentStage(2);
    }
    if (HTSCode) {
      setCurrentStage(3);
    }
  }, [followupItems, cmdtDesc, HTSCode]);

  const submitClassification = async (
    product_desc: string,
    followups: iFollowupItem[],
  ) => {
    const clf = {
      clf_id: clfId,
      product_desc: product_desc,
      followup_items: followups,
    };
    connectWebSocket('/ws/eccn_classify', clf);
  };

  const handleSubmit = async (inputValue: string) => {
    if (inputValue !== '') {
      setUserProductDesc(inputValue);
      setCurrentStage(1);
      setIsLoading(true);
      try {
        await submitClassification(inputValue, followupItems);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
      }
    } else {
      enqueueSnackbar('Please input something to classify', {
        variant: 'error',
      });
    }
  };

  const handleFollowupSubmit = (responses: iFollowupItem[]) => {
    if (userProductDesc !== '') {
      setIsLoading(true);
      setFollowupItems(responses);
      submitClassification(userProductDesc, responses);
      setIsLoading(false);
    } else {
      enqueueSnackbar('Please input something to classify', {
        variant: 'error',
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleReset = () => {
    if (autoTab === 'ECCN') {
      setCurrentStage(0);
      setHTSCode('');
      setCmdtDesc('');
      setFollowupItems([]);
      setUserProductDesc('');
      setTicketID('');
    } else {
      setCurrentEStage(0);
      setTicketID('');
    }
  };

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const handleTabSwitch = (newTab: string) => {
    switch (newTab) {
      case 'ECCN':
        setAutoTab('ECCN');
        window.sessionStorage.setItem('autoTab', 'ECCN');
        break;
      case 'Export Control Classification Number':
        setAutoTab('Export Control Classification Number');
        window.sessionStorage.setItem(
          'autoTab',
          'Export Control Classification Number',
        );
        break;
      default:
        setAutoTab('ECCN');
    }
  };

  const handleTicket = async (values: any) => {
    const new_case = {
      groupId: cur_user.selected_team.id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.createCase({ newCase: new_case });

    if (resp?.success) {
      setTicketID(resp.result._id);
      enqueueSnackbar('Successfully saved work', { variant: 'success' });
      shareEmail.forEach((email) => {
        // Call to notification sending function here
        handleShareTicketNotification(
          values.ticketName,
          cur_user,
          email,
          resp.result._id,
        );
      });
      setSavingTicket(false);
      setModalOpen(false);
      setNewTicketName('');
    } else if (resp) {
      const error_msg =
        'Error saving, please try again and contact the BITE Team if the problem persist';
      enqueueSnackbar(error_msg, { variant: 'error' });
      setSavingTicket(false);
      setModalOpen(false);
    }
  };

  const saveTicket = async (name: string) => {
    setSavingTicket(true);
    const ownerQuery = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };

    const query: any = {
      owner: ownerQuery,
      ticketName: name,
      lastUpdate: formatted_dt,
      docType: 'ECCN',
      sharedWith: shareEmail,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team.name,
      classificationValue: userProductDesc,
      followUps: followupItems,
      aiStream: streamData,
      exNotes: explanatoryNotes,
      chapterNotes: sectionNotes,
      crossRulings: crossLinks,
      commodity: {
        commodityName: cmdtDesc,
        eccn: HTSCode,
      },
      activity: {
        user: {
          kc_id: cur_user.basics?.id,
          email: cur_user.basics?.email,
          firstName: cur_user.basics?.firstName,
          lastName: cur_user.basics?.lastName,
        },
        action: 'saved classification',
        timeStamp: Date.now(),
      },
    };
    handleTicket(query);
  };

  const formatExplanatoryNote = (rawNotes: string) => {
    const cleanedNotes = rawNotes
      .replace(/[\[\]"]/g, '')
      .replace(/,(\s)?$/gm, '')
      .replace(/^°/gm, '');

    const lines = cleanedNotes
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const result: JSX.Element[] = [];
    let bulletItems: string[] = [];

    const flushBullets = () => {
      if (bulletItems.length > 0) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            style={{ margin: '0.25rem 0', paddingLeft: '1.25rem' }}
          >
            {bulletItems.map((item, i) => (
              <li
                key={i}
                style={{ marginBottom: '0.25rem' }}
                dangerouslySetInnerHTML={{
                  __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'),
                }}
              />
            ))}
          </ul>,
        );
        bulletItems = [];
      }
    };

    lines.forEach((line, index) => {
      const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      const isHeading =
        /^\*\*/.test(line) || (/^[A-Z\s]+$/.test(line) && line.length < 80);
      const isBullet = /^[-•*]|\(\w\)|^\(\d+\)|^\d+\)/.test(line);

      if (isHeading) {
        flushBullets();
        result.push(
          <h2
            key={`h-${index}`}
            style={{ margin: '0.5rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      } else if (isBullet) {
        bulletItems.push(formatted);
      } else {
        flushBullets();
        result.push(
          <p
            key={`p-${index}`}
            style={{ margin: '0.25rem 0', fontSize: '12px' }}
            dangerouslySetInnerHTML={{ __html: formatted }}
          />,
        );
      }
    });

    flushBullets();

    return result;
  };

  return (
    <>
      <section
        className='home-content-body Home-HeaderDown'
        style={{ minHeight: 0 }}
      >
        <Grid container>
          <Grid item xs={12}>
            <div className='bill-row'>
              <div className='bill-containers'>
                <div className='bill-container-header'>
                  <div className='bill-container-header-bread-crumbs'>
                    <div className='bill-container-header-bread-crumbs-tabs'>
                      <Link to='/'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='20'
                          height='20'
                          viewBox='0 0 20 20'
                          fill='none'
                          cursor={'pointer'}
                        >
                          <path
                            d='M7.5 17.5V11.3333C7.5 10.8666 7.5 10.6333 7.59083 10.455C7.67072 10.2982 7.79821 10.1707 7.95501 10.0908C8.13327 9.99999 8.36662 9.99999 8.83333 9.99999H11.1667C11.6334 9.99999 11.8667 9.99999 12.045 10.0908C12.2018 10.1707 12.3293 10.2982 12.4092 10.455C12.5 10.6333 12.5 10.8666 12.5 11.3333V17.5M9.18141 2.30333L3.52949 6.69927C3.15168 6.99312 2.96278 7.14005 2.82669 7.32405C2.70614 7.48704 2.61633 7.67065 2.56169 7.86588C2.5 8.08627 2.5 8.32558 2.5 8.80421V14.8333C2.5 15.7667 2.5 16.2335 2.68166 16.59C2.84144 16.9036 3.09641 17.1585 3.41002 17.3183C3.76654 17.5 4.23325 17.5 5.16667 17.5H14.8333C15.7668 17.5 16.2335 17.5 16.59 17.3183C16.9036 17.1585 17.1586 16.9036 17.3183 16.59C17.5 16.2335 17.5 15.7667 17.5 14.8333V8.80421C17.5 8.32558 17.5 8.08627 17.4383 7.86588C17.3837 7.67065 17.2939 7.48704 17.1733 7.32405C17.0372 7.14005 16.8483 6.99312 16.4705 6.69927L10.8186 2.30333C10.5258 2.07562 10.3794 1.96177 10.2178 1.918C10.0752 1.87938 9.92484 1.87938 9.78221 1.918C9.62057 1.96177 9.47418 2.07562 9.18141 2.30333Z'
                            stroke='#4895EF'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </Link>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        viewBox='0 0 16 16'
                        fill='none'
                      >
                        <path
                          d='M6 12L10 8L6 4'
                          stroke='var(--card-text)'
                          strokeWidth='1.33333'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                      <div className='bc-title'>{autoTab}</div>
                    </div>
                  </div>
                </div>
                <div
                  className={`bill-container-body ${
                    isExpanded || isExpandedCross ? 'expanded' : ''
                  }`}
                  style={{ alignItems: 'flex-start', gap: '10px' }}
                >
                  <div className='screening-tabs-container'>
                    <div className='screening-tabs-inner'>
                      <div
                        className={`screening-tab-btn${
                          autoTab === 'ECCN' ? '-selected' : ''
                        }`}
                        onClick={() => handleTabSwitch('ECCN')}
                      >
                        <div
                          className={`screening-tab-btn-text${
                            autoTab === 'ECCN' ? '-selected' : ''
                          }`}
                        >
                          {autoTab === 'ECCN' ? (
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                              style={{ marginRight: '5px' }}
                            >
                              <path
                                d='M15.75 15.75L11.2501 11.25M12.75 7.5C12.75 10.3995 10.3995 12.75 7.5 12.75C4.6005 12.75 2.25 10.3995 2.25 7.5C2.25 4.6005 4.6005 2.25 7.5 2.25C10.3995 2.25 12.75 4.6005 12.75 7.5Z'
                                stroke='#4895EF'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='18'
                              height='18'
                              viewBox='0 0 18 18'
                              fill='none'
                              style={{ marginRight: '5px' }}
                            >
                              <path
                                d='M15.75 15.75L11.2501 11.25M12.75 7.5C12.75 10.3995 10.3995 12.75 7.5 12.75C4.6005 12.75 2.25 10.3995 2.25 7.5C2.25 4.6005 4.6005 2.25 7.5 2.25C10.3995 2.25 12.75 4.6005 12.75 7.5Z'
                                stroke='var(--card-text)'
                                strokeWidth='1.5'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              />
                            </svg>
                          )}
                          ECCN
                        </div>
                      </div>
                    </div>
                    <div className='module-header-title-conts'>
                      <Tooltip
                        title={'Clear'}
                        placement='bottom-start'
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              display: 'flex',
                              padding:
                                'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              alignSelf: 'stretch',
                              borderRadius: 'var(--radius-md, 8px)',
                              background:
                                'var(--Background-200, var(--bg-colors))',
                              color: 'var(--text-prim25)',
                              maxWidth: '700px',
                              '& .MuiTooltip-arrow': {
                                color:
                                  'var(--Background-200, var(--bg-colors))',
                              },
                            },
                          },
                        }}
                      >
                        <div className='audit-btn-star' onClick={handleReset}>
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                          >
                            <path
                              d='M17.5003 8.33333C17.5003 8.33333 15.8295 6.05685 14.4721 4.69854C13.1148 3.34022 11.239 2.5 9.16699 2.5C5.02486 2.5 1.66699 5.85786 1.66699 10C1.66699 14.1421 5.02486 17.5 9.16699 17.5C12.5862 17.5 15.4711 15.2119 16.3739 12.0833M17.5003 8.33333V3.33333M17.5003 8.33333H12.5003'
                              stroke='var(--card-text)'
                              strokeWidth='1.66667'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </Tooltip>
                      <Tooltip
                        title={'Save Work'}
                        placement='bottom-start'
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              display: 'flex',
                              padding:
                                'var(--spacing-md, 8px) var(--spacing-lg, 12px)',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              alignSelf: 'stretch',
                              borderRadius: 'var(--radius-md, 8px)',
                              background:
                                'var(--Background-200, var(--bg-colors))',
                              color: 'var(--text-prim25)',
                              maxWidth: '700px',
                              '& .MuiTooltip-arrow': {
                                color:
                                  'var(--Background-200, var(--bg-colors))',
                              },
                            },
                          },
                        }}
                      >
                        <div
                          className='audit-btn-star'
                          onClick={() => {
                            if (
                              cmdtDesc &&
                              HTSCode &&
                              cmdtDesc.length > 0 &&
                              HTSCode.length > 0
                            ) {
                              // setModalOpen(true);
                            }
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            width='20'
                            height='20'
                            viewBox='0 0 20 20'
                            fill='none'
                          >
                            <path
                              d='M5.83333 2.5V5.33333C5.83333 5.80004 5.83333 6.0334 5.92416 6.21166C6.00406 6.36846 6.13154 6.49594 6.28834 6.57584C6.4666 6.66667 6.69996 6.66667 7.16667 6.66667H12.8333C13.3 6.66667 13.5334 6.66667 13.7117 6.57584C13.8685 6.49594 13.9959 6.36846 14.0758 6.21166C14.1667 6.0334 14.1667 5.80004 14.1667 5.33333V3.33333M14.1667 17.5V12.1667C14.1667 11.7 14.1667 11.4666 14.0758 11.2883C13.9959 11.1315 13.8685 11.0041 13.7117 10.9242C13.5334 10.8333 13.3 10.8333 12.8333 10.8333H7.16667C6.69996 10.8333 6.4666 10.8333 6.28834 10.9242C6.13154 11.0041 6.00406 11.1315 5.92416 11.2883C5.83333 11.4666 5.83333 11.7 5.83333 12.1667V17.5M17.5 7.77124V13.5C17.5 14.9001 17.5 15.6002 17.2275 16.135C16.9878 16.6054 16.6054 16.9878 16.135 17.2275C15.6002 17.5 14.9001 17.5 13.5 17.5H6.5C5.09987 17.5 4.3998 17.5 3.86502 17.2275C3.39462 16.9878 3.01217 16.6054 2.77248 16.135C2.5 15.6002 2.5 14.9001 2.5 13.5V6.5C2.5 5.09987 2.5 4.3998 2.77248 3.86502C3.01217 3.39462 3.39462 3.01217 3.86502 2.77248C4.3998 2.5 5.09987 2.5 6.5 2.5H12.2288C12.6364 2.5 12.8402 2.5 13.0321 2.54605C13.2021 2.58688 13.3647 2.65422 13.5138 2.7456C13.682 2.84867 13.8261 2.9928 14.1144 3.28105L16.719 5.88562C17.0072 6.17387 17.1513 6.318 17.2544 6.48619C17.3458 6.63531 17.4131 6.79789 17.4539 6.96795C17.5 7.15976 17.5 7.36358 17.5 7.77124Z'
                              stroke='var(--card-text)'
                              strokeWidth='1.66667'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                        </div>
                      </Tooltip>
                    </div>
                  </div>

                  {autoTab === 'ECCN' && (
                    <>
                      <div className='disclaimer-text'>
                        The ECCN Classification provided is for general
                        informational purposes only and does not constitute
                        legal advice or a binding determination.
                      </div>
                      {currentStage === 0 && (
                        <InputForm
                          onSubmit={handleSubmit}
                          isStreaming={isStreaming}
                          initialValue={userProductDesc}
                        />
                      )}
                      {currentStage === 1 && (
                        <div className='classify'>
                          <div className='classify-header'>
                            Product Name / Product Description
                          </div>
                          <div className='classify-cont'>
                            <div className='classify-cont-sec'>
                              <div className='classify-cont-sec-div'>
                                <textarea
                                  placeholder={userProductDesc}
                                  className='classify-cont-sec-input'
                                  rows={6}
                                  onInput={(e) => {
                                    const target =
                                      e.target as HTMLTextAreaElement; // type assertion
                                    target.style.height = 'auto';
                                    target.style.height = `${target.scrollHeight}px`;
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                          <LoadingFeedback size='small' />
                          <div className='classify-footer'>
                            <div className='classify-footer-btn-loading'>
                              <div className='classify-footer-btn-div'>
                                Reasoning...
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {currentStage === 2 && (
                        <FollowupQuestionsDisplay
                          followupItems={followupItems}
                          isStreaming={isStreaming}
                          onSubmitFollowup={handleFollowupSubmit}
                          userProductDesc={userProductDesc}
                        />
                      )}
                      {currentStage === 3 && (
                        <>
                          <div
                            style={{
                              paddingTop: '10px',
                              color: 'var(--Text-Main, var(--text-primary))',
                              fontSize: '14px',
                              fontWeight: '600',
                            }}
                          >
                            <div>
                              <h2
                                style={{
                                  color:
                                    'var(--Text-Main, var(--text-primary))',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                }}
                              >
                                ECCN Code:{' '}
                              </h2>
                              {HTSCode && (
                                <h2
                                  style={{
                                    color:
                                      'var(--Text-Main, var(--text-primary))',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                  }}
                                >
                                  {HTSCode}
                                </h2>
                              )}
                            </div>
                            <div>
                              <h2
                                style={{
                                  color:
                                    'var(--Text-Main, var(--text-primary))',
                                  fontSize: '14px',
                                  fontWeight: '600',
                                }}
                              >
                                Product Description:{' '}
                              </h2>
                              {cmdtDesc && (
                                <h2
                                  style={{
                                    color:
                                      'var(--Text-Main, var(--text-primary))',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                  }}
                                >
                                  {cmdtDesc}
                                </h2>
                              )}
                            </div>
                          </div>
                          <div className='classify-footer'>
                            <div
                              className='classify-footer-btn'
                              onClick={() => setModalOpen(true)}
                            >
                              <div className='classify-footer-btn-divs'>
                                <svg
                                  xmlns='http://www.w3.org/2000/svg'
                                  width='18'
                                  height='18'
                                  viewBox='0 0 18 18'
                                  fill='none'
                                >
                                  <path
                                    d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                                    stroke='var(--text-primary)'
                                    strokeWidth='1.66667'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                  />
                                </svg>
                                Save as Ticket
                              </div>
                            </div>
                            <div
                              className='classify-footer-btn'
                              onClick={handleReset}
                            >
                              <div className='classify-footer-btn-divs'>
                                New Search
                              </div>
                            </div>
                            <Link
                              to={{
                                pathname: '/bite-eccn',
                                state: {
                                  primary: HTSCode ? HTSCode : '',
                                },
                              }}
                            >
                              <div className='classify-footer-btn'>
                                <div className='classify-footer-btn-divs'>
                                  Check Control
                                </div>
                              </div>
                            </Link>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
              <div
                className='bill-container-fit-screens-classify'
                style={{ minHeight: '860px' }}
              >
                {autoTab === 'ECCN' &&
                  (currentStage === 0 ? (
                    <div className='classify-results'>
                      Enter information to see results. The more information you
                      provide, the more accurate the results will be.
                    </div>
                  ) : (
                    <div className='classify-results-card-list'>
                      <StreamDisplay
                        streamData={streamData}
                        ticket={false}
                        module='ear'
                      />
                    </div>
                  ))}

                <div className='classify-container-footer'>
                  <div
                    className='classify-container-footer-clear'
                    onClick={handleReset}
                  >
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='18'
                      height='18'
                      viewBox='0 0 18 18'
                      fill='none'
                    >
                      <path
                        d='M15.75 7.5C15.75 7.5 14.2463 5.45116 13.0246 4.22868C11.803 3.0062 10.1148 2.25 8.25 2.25C4.52208 2.25 1.5 5.27208 1.5 9C1.5 12.7279 4.52208 15.75 8.25 15.75C11.3273 15.75 13.9237 13.6907 14.7362 10.875M15.75 7.5V3M15.75 7.5H11.25'
                        stroke='var(--card-text)'
                        strokeWidth='1.66667'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                      />
                    </svg>
                    <div className='classify-container-footer-clear-text'>
                      Clear
                    </div>
                  </div>
                  {HTSCode?.length === 0 ||
                  cmdtDesc?.length === 0 ||
                  !HTSCode ||
                  !cmdtDesc ? (
                    <div className='classify-container-footer-btn-save'>
                      <div className='classify-container-footer-btn-result-text'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='none'
                        >
                          <path
                            d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                            stroke='var(--card-text)'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        Save
                      </div>
                    </div>
                  ) : !ticketId ? (
                    <div
                      className='active-save-btn'
                      onClick={() => setModalOpen(true)}
                    >
                      <div className='active-text'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='18'
                          height='18'
                          viewBox='0 0 18 18'
                          fill='none'
                        >
                          <path
                            d='M5.25 2.25V4.8C5.25 5.22004 5.25 5.43006 5.33175 5.59049C5.40365 5.73161 5.51839 5.84635 5.65951 5.91825C5.81994 6 6.02996 6 6.45 6H11.55C11.97 6 12.1801 6 12.3405 5.91825C12.4816 5.84635 12.5964 5.73161 12.6683 5.59049C12.75 5.43006 12.75 5.22004 12.75 4.8V3M12.75 15.75V10.95C12.75 10.53 12.75 10.3199 12.6683 10.1595C12.5964 10.0184 12.4816 9.90365 12.3405 9.83175C12.1801 9.75 11.97 9.75 11.55 9.75H6.45C6.02996 9.75 5.81994 9.75 5.65951 9.83175C5.51839 9.90365 5.40365 10.0184 5.33175 10.1595C5.25 10.3199 5.25 10.53 5.25 10.95V15.75M15.75 6.99411V12.15C15.75 13.4101 15.75 14.0402 15.5048 14.5215C15.289 14.9448 14.9448 15.289 14.5215 15.5048C14.0402 15.75 13.4101 15.75 12.15 15.75H5.85C4.58988 15.75 3.95982 15.75 3.47852 15.5048C3.05516 15.289 2.71095 14.9448 2.49524 14.5215C2.25 14.0402 2.25 13.4101 2.25 12.15V5.85C2.25 4.58988 2.25 3.95982 2.49524 3.47852C2.71095 3.05516 3.05516 2.71095 3.47852 2.49524C3.95982 2.25 4.58988 2.25 5.85 2.25H11.0059C11.3728 2.25 11.5562 2.25 11.7288 2.29145C11.8819 2.32819 12.0282 2.3888 12.1624 2.47104C12.3138 2.5638 12.4435 2.69352 12.7029 2.95294L15.0471 5.29706C15.3065 5.55648 15.4362 5.6862 15.529 5.83757C15.6112 5.97178 15.6718 6.1181 15.7086 6.27115C15.75 6.44378 15.75 6.62723 15.75 6.99411Z'
                            stroke='var(--text-primary)'
                            strokeWidth='1.66667'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                        Save
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={`/monitor/ticket/${ticketId}`}
                      style={{ flex: '1 0' }}
                    >
                      <div className='active-save-btn'>
                        <div className='active-text'>View Saved</div>
                      </div>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </Grid>
        </Grid>
        <Modal
          open={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
        >
          <DeleteCommonModal
            handleClose={() => setIsDeleteModalOpen(false)}
            title='Delete search?'
            paragraph='This will permanently remove this search from your group list.'
          />
        </Modal>
        <Modal open={isModalOpen} onClose={handleCloseModal}>
          <CreateTicketWizard
            cur_user={cur_user}
            handleSaveTicket={saveTicket}
            newTicketName={newTicketName}
            setNewTicketName={setNewTicketName}
            shareEmail={shareEmail}
            setShareEmail={setShareEmail}
            savingTicket={savingTicket}
          />
        </Modal>
      </section>
    </>
  );
};

export default EARClassification;
